import { Button, notification } from 'antd'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SourceStage } from '~app/graph/generated/graphql'
import { usePaginatedSources } from '~app/graph/source/withPagination'
import SourcesFilter from './SourcesFilter'
import SourcesTable from './SourcesTable'
import UploadCancellationCSVButton from './UploadCancellationCSVButton'
import UploadCSVButton from './UploadCSVButton'

notification.config({ placement: 'bottomRight' })

const defaultStages = [
  SourceStage.Requested,
  SourceStage.Cancelling,
  SourceStage.Renewing,
]
interface SearchState {
  id?: string
  publicId?: string
  customer?: string
  address?: string
  stages?: SourceStage[]
}

/*
 * Component
 */

const SourcesRoute = () => {
  const { paginationQuery } = usePaginatedSources()
  const { loading, sources, next, refetch } = paginationQuery
  const { t } = useTranslation()
  /**
   * TODO: review search state structure and wether to memoize it
   * TODO: debounce this call ?
   */
  const [searchQuery, setSearchQuery] = useState<SearchState>({
    stages: defaultStages,
  })

  useEffect(() => {
    refetch({
      where: {
        AND: [
          searchQuery.id
            ? {
                id_contains: searchQuery.id,
              }
            : {},
          searchQuery.publicId
            ? {
                customer: {
                  publicId: Number.parseInt(searchQuery.publicId, 0),
                },
              }
            : {},
          searchQuery.address
            ? {
                address: {
                  OR: [
                    { city_contains: searchQuery.address },
                    { streetAddress_contains: searchQuery.address },
                    { zip_contains: searchQuery.address },
                  ],
                },
              }
            : {},
          searchQuery.customer
            ? {
                customer: {
                  OR: [
                    { companyName_contains: searchQuery.customer },
                    { firstName_contains: searchQuery.customer },
                    { lastName_contains: searchQuery.customer },
                    { id_contains: searchQuery.customer },
                  ],
                },
              }
            : {},
          searchQuery.stages
            ? {
                stage_in: searchQuery.stages,
              }
            : {},
        ],
      },
    })
  }, [searchQuery])

  const onCSVUploadError = (error, durationSeconds = 0) =>
    notification.error({
      message: 'Uh oh.',
      description: error.message || t('sources.somethingWentWrongMsg'),
      duration: durationSeconds,
    })

  const onCSVUploadComplete = () => {
    notification.info({
      message: t('sources.CSVUploadCompleteTitle'),
      description: t('sources.CSVUploadCompleteBody'),
    })
    refetch()
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <UploadCSVButton
          onError={onCSVUploadError}
          onUploadComplete={onCSVUploadComplete}
        />
        <UploadCancellationCSVButton
          onError={onCSVUploadError}
          onUploadComplete={onCSVUploadComplete}
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <SourcesFilter
          onFilter={state => {
            if (isEmpty(state)) {
              setSearchQuery({ stages: defaultStages })
            } else {
              setSearchQuery({
                ...searchQuery,
                ...state,
              })
            }
          }}
        />
      </div>
      <SourcesTable
        loading={loading}
        refetchData={refetch}
        stages={searchQuery.stages}
        sources={sources}
        onChange={(_, filters: any /* TODO: why any? */, __) => {
          setSearchQuery({
            ...searchQuery,
            stages: filters.actions,
          })
        }}
      />
      {next && ( // TODO: move into table
        <Button type="primary" block loading={loading} onClick={() => next()}>
          {t('returns.loadMore')}
        </Button>
      )}
    </div>
  )
}
export default SourcesRoute
